import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import * as Icons from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import SEOHead from "@/components/site/SEOHead";
import Reveal from "@/components/site/Reveal";
import CTA from "@/components/site/CTA";
import SmartMedia from "@/components/site/SmartMedia";
import { collectAttribution, trackEvent } from "@/lib/settings";
import { usePageContent } from "@/lib/content";

const Icon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.Sparkles;
  return <C {...p} />;
};

export default function PartnerWithUs() {
  const { content: c } = usePageContent("partner");
  const [form, setForm] = useState({
    name: "", phone: "", email: "", property_name: "", property_location: "",
    property_size: "", ownership: "", interest: "Operating partnership", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name || !form.phone) { toast.error("Name and phone are required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name, phone: form.phone, email: form.email,
        event_type: "Property Partnership",
        property_interested: form.property_name || "Owner enquiry",
        guest_count: form.property_size,
        message: `[Owner] Property: ${form.property_name} · Location: ${form.property_location} · Size: ${form.property_size} · Ownership: ${form.ownership} · Interest: ${form.interest} · Notes: ${form.message}`,
        ...collectAttribution(),
      };
      await api.post("/public/leads", payload);
      trackEvent("partner_submit", { property: form.property_name, interest: form.interest });
      setDone(true);
      toast.success("Submitted. Our partnerships team will reach out within 48 hours.");
    } catch { toast.error("Could not submit. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (!c) return <div className="pt-32 pb-32 container-x"><div className="animate-pulse h-96 bg-bone" /></div>;

  const hero = c.hero || {}, stats = c.stats || {}, phi = c.philosophy || {},
        pill = c.pillars || {}, stp = c.steps || {}, mdl = c.models || {}, enq = c.enquire || {};

  const schema = { "@context": "https://schema.org", "@type": "Organization", name: "Happy Retreats",
    url: typeof window !== "undefined" ? window.location.origin : "",
    description: hero.subheadline || "" };

  return (
    <>
      <SEOHead title="Partner with Happy Retreats — Hospitality Operator for Celebration Getaways"
        description={hero.subheadline}
        keywords="hospitality operator india, partner with happy retreats, wedding venue operator bangalore"
        schema={schema} />

      {/* HERO */}
      {hero.enabled !== false && (
        <section className="relative min-h-[90svh] flex items-end overflow-hidden">
          <SmartMedia media={hero.image_url} alt="Partner" className="absolute inset-0 w-full h-full object-cover" eager />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/20 to-ink/85" />
          <div className="relative container-x pb-20 md:pb-32 pt-44 md:pt-52 text-ivory">
            {hero.overline && <div className="overline text-ivory/85 animate-fade-up">{hero.overline}</div>}
            <h1 className="display-1 mt-8 max-w-5xl animate-fade-up" style={{ animationDelay: "0.2s" }}>
              {hero.headline_pre}{hero.headline_accent ? <span className="accent-block"><span className="italic text-clay">{hero.headline_accent}</span>{hero.headline_post}</span> : hero.headline_post}
            </h1>
            {hero.subheadline && <p className="mt-8 max-w-2xl text-base md:text-lg text-ivory/85 font-light leading-relaxed animate-fade-up" style={{ animationDelay: "0.4s" }}>{hero.subheadline}</p>}
            <div className="mt-12 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "0.6s" }}>
              <CTA config={hero.primary_cta} variant="onDarkPrimary" testId="partner-hero-cta" />
              <CTA config={hero.secondary_cta} variant="onDarkGhost" />
            </div>
          </div>
        </section>
      )}

      {/* REVENUE CALCULATOR STRIP */}
      <section className="bg-clay/10 border-b border-clay/20">
        <div className="container-x py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-clay shrink-0" />
            <p className="text-sm text-ink">
              <span className="font-medium">Curious what your property could earn?</span>{" "}
              <span className="text-graphite">Project your annual revenue across stays, weddings and socials — full transparency, the Happy Retreats way.</span>
            </p>
          </div>
          <Link to="/revenue-calculator" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-medium text-ink border border-ink px-5 py-2.5 hover:bg-ink hover:text-ivory transition-colors" data-testid="partner-revenue-calc-link">
            Revenue calculator <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* STATS */}
      {stats.enabled !== false && stats.items?.length > 0 && (
        <section id="stats" className="bg-ink text-ivory border-y border-ivory/10" data-testid="partner-stats">
          <div className="container-x grid grid-cols-2 md:grid-cols-4 divide-x divide-ivory/10">
            {stats.items.map((s, i) => (
              <Reveal key={i} delay={i * 80} className="py-10 md:py-12 px-6 first:pl-0 last:pr-0">
                <div className="font-display text-3xl md:text-4xl lg:text-5xl">{s.v}</div>
                <div className="mt-3 text-[10px] md:text-xs text-ivory/60 uppercase tracking-[0.18em] leading-snug">{s.l}</div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* PHILOSOPHY */}
      {phi.enabled !== false && (
        <section id="philosophy" className="container-x py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-5">
              {phi.overline && <div className="overline">{phi.overline}</div>}
              <h2 className="display-2 mt-6">{phi.headline_line1}{phi.headline_line2 && <span className="accent-block">{phi.headline_line2}</span>}</h2>
            </Reveal>
            <Reveal delay={150} className="lg:col-span-6 lg:col-start-7 para-stack body-lead">
              {(phi.paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
            </Reveal>
          </div>
        </section>
      )}

      {/* PILLARS */}
      {pill.enabled !== false && pill.items?.length > 0 && (
        <section id="pillars" className="bg-bone py-8 md:py-12">
          <div className="container-x">
            {pill.overline && <Reveal><div className="overline">{pill.overline}</div></Reveal>}
            {pill.headline && <Reveal delay={100}><h2 className="display-2 mt-6 max-w-3xl">{pill.headline}</h2></Reveal>}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10">
              {pill.items.map((p, i) => (
                <Reveal key={i} delay={i * 70}>
                  <div className="p-8 bg-ivory h-full">
                    <Icon name={p.icon} className="w-6 h-6 text-clay" strokeWidth={1.4} />
                    <div className="number-marker mt-6">0{i + 1}</div>
                    <div className="font-display text-2xl mt-3">{p.title}</div>
                    <div className="mt-3 text-sm text-graphite leading-relaxed font-light">{p.body}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* STEPS */}
      {stp.enabled !== false && stp.items?.length > 0 && (
        <section className="container-x py-8 md:py-12">
          <div className="mb-14">
            {stp.overline && <Reveal><div className="overline">{stp.overline}</div></Reveal>}
            {stp.headline && <Reveal delay={100}><h2 className="display-2 mt-6 max-w-2xl">{stp.headline}</h2></Reveal>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10">
            {stp.items.map((s, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="bg-ivory p-8 h-full">
                  <div className="font-mono text-[11px] tracking-[0.2em] text-clay">STEP {s.kw}</div>
                  <div className="font-display text-2xl mt-4">{s.title}</div>
                  <div className="mt-3 text-sm text-graphite leading-relaxed font-light">{s.body}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* MODELS */}
      {mdl.enabled !== false && mdl.items?.length > 0 && (
        <section id="models" className="bg-ink text-ivory py-8 md:py-12">
          <div className="container-x">
            {mdl.overline && <Reveal><div className="overline text-ivory/60">{mdl.overline}</div></Reveal>}
            {mdl.headline && <Reveal delay={100}><h2 className="display-2 mt-6 text-ivory max-w-3xl">{mdl.headline}</h2></Reveal>}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-ivory/10">
              {mdl.items.map((m, i) => (
                <Reveal key={i} delay={i * 100}>
                  <div className="bg-ink p-10 h-full">
                    <div className="overline text-clay">Model · 0{i + 1}</div>
                    <div className="font-display text-3xl mt-6 text-ivory">{m.name}</div>
                    <div className="hairline bg-ivory/15 my-6" />
                    <p className="text-sm text-ivory/80 leading-relaxed">{m.caption}</p>
                    <p className="text-xs text-clay mt-6 italic">{m.best}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ENQUIRE */}
      {enq.enabled !== false && (
        <section id="enquire" className="container-x py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              {enq.overline && <Reveal><div className="overline">{enq.overline}</div></Reveal>}
              {enq.headline && <Reveal delay={100}><h2 className="display-2 mt-6">{enq.headline}</h2></Reveal>}
              {enq.body && <Reveal delay={200}><p className="body-lead mt-8 max-w-md">{enq.body}</p></Reveal>}
              <Reveal delay={300}>
                <div className="mt-10 space-y-4 text-sm">
                  {(enq.assurances || []).map((a, i) => (
                    <div key={i} className="flex items-start gap-3">
                      {i === 0 ? <ShieldCheck className="w-5 h-5 text-clay mt-0.5" /> : <Sparkles className="w-5 h-5 text-clay mt-0.5" />}
                      <div><div className="font-medium">{a.title}</div><div className="text-graphite text-xs">{a.sub}</div></div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
            <Reveal delay={200} className="lg:col-span-6 lg:col-start-7">
              {done ? (
                <div className="bg-bone p-10 md:p-14">
                  <div className="overline">Submitted</div>
                  <h3 className="display-2 mt-4">Thank you, {form.name.split(" ")[0]}.</h3>
                  <p className="body-lead mt-6">Your property submission is with our partnerships team. Expect a personal call within 48 hours.</p>
                  <Link to="/" className="link-arrow mt-8 inline-flex">Return home <ArrowRight className="w-4 h-4" /></Link>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-bone p-8 md:p-12" data-testid="partner-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input value={form.name} onChange={onChange("name")} placeholder="Your name *" required data-testid="partner-name" />
                    <Input value={form.phone} onChange={onChange("phone")} placeholder="Phone *" required data-testid="partner-phone" />
                  </div>
                  <Input value={form.email} onChange={onChange("email")} placeholder="Email" type="email" className="mt-4" data-testid="partner-email" />
                  <div className="hairline my-6" />
                  <Input value={form.property_name} onChange={onChange("property_name")} placeholder="Property name" data-testid="partner-property-name" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Input value={form.property_location} onChange={onChange("property_location")} placeholder="Location" data-testid="partner-property-location" />
                    <Input value={form.property_size} onChange={onChange("property_size")} placeholder="Size (acres / rooms)" data-testid="partner-property-size" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Select value={form.ownership} onValueChange={onChange("ownership")}>
                      <SelectTrigger data-testid="partner-ownership"><SelectValue placeholder="Ownership status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sole owner">Sole owner</SelectItem>
                        <SelectItem value="Joint">Joint / family</SelectItem>
                        <SelectItem value="Company">Owned by company</SelectItem>
                        <SelectItem value="Under construction">Under construction</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={form.interest} onValueChange={onChange("interest")}>
                      <SelectTrigger data-testid="partner-interest"><SelectValue placeholder="Preferred model" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Operating partnership">Operating partnership</SelectItem>
                        <SelectItem value="Lease & operate">Lease & operate</SelectItem>
                        <SelectItem value="Hybrid">Hybrid</SelectItem>
                        <SelectItem value="Open to suggestions">Open to suggestions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea value={form.message} onChange={onChange("message")} placeholder="Anything else we should know?" rows={4} className="mt-4" data-testid="partner-message" />
                  <button type="submit" disabled={submitting} className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="partner-submit">
                    {submitting ? "Sending…" : "Submit my property"}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </section>
      )}
    </>
  );
}
